import React, { useState } from 'react';
import { Box, Flex, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import {
  setTransportationForm
} from '../../../store/slices/transportation/transportationSlice';
import { RootState } from '../../../store/store';
import { getTranslationByPath } from '../../../utils/translations';
import { addNotification } from '../../../store/slices/notification/notificationSlice';
import { MainContent } from '../../../layout/content/MainContent';
import { KeyboardWrapper } from '../../../components/keyboard/KeyboardWrapper';
import '../announcementForm/style.css';
import { findDriverByDocument } from '../../../apollo/queries/findDriverByDocument';
import { sendAuthNotification } from '../../../apollo/mutations/sendAuthNotification';

export type UserBozpTemplateType = {
  transitionUrl: TransitionUrl
  phoneNumberInputLabel: TranslationPath
  phoneNumberInputPlaceholder: TranslationPath
  documentTypeInputLabel: TranslationPath
  documentTypeInputPlaceholder: TranslationPath
  documentTypeIdCardLabel: TranslationPath
  documentTypePassportLabel: TranslationPath
  documentNumberInputLabel: TranslationPath
  documentNumberInputPlaceholder: TranslationPath
  invalidDocumentNumberText: TranslationPath
  notFoundText: TranslationPath
}

type Props = {
  page: Page<UserBozpTemplateType>
}

export const UserBozpTemplate = (props: Props): React.ReactElement => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [documentType, setDocumentType] = useState<'idCard' | 'passport'>('idCard');
  const [phoneNumberValue, setPhoneNumberValue] = useState<string>('');
  const [documentNumber, setDocumentNumber] = useState<string>('');

  const language = useLanguage();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation);
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm);

  const onContinue = async () => {
    if (!fetchedTransportation) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.notFoundText) },
      ));
      return;
    }
    if (documentNumber.trim().length === 0) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidDocumentNumberText) },
      ));
      return;
    }

    setIsProcessing(true);

    let foundDriver = null;
    try {
      const driverResponse = await findDriverByDocument({
        driverIdNo: documentNumber.trim(),
        driverIdType: documentType,
        phoneNumber: phoneNumberValue || fetchedTransportation.phoneNumber || '',
      });
      foundDriver = driverResponse.data.findDriverByDocument;
    } catch (e) {
      foundDriver = null;
    }
    const resolvedPhoneNumber = foundDriver
      ? (foundDriver.phoneNumber || phoneNumberValue || fetchedTransportation.phoneNumber || '')
      : (phoneNumberValue || fetchedTransportation.phoneNumber || '');
    const resolvedDriverName = foundDriver ? (foundDriver.driverName || '') : '';
    const resolvedDocumentNumber = foundDriver
      ? (foundDriver.driverIdNo || documentNumber.trim())
      : documentNumber.trim();
    if (resolvedPhoneNumber) {
      try {
        await sendAuthNotification({
          qtirLanguageId: language.qtirLanguageId,
          phoneNumber: resolvedPhoneNumber,
          newPin: true,
        });
      } catch (e) {
        // Keep flow non-blocking; document lookup result is still valid.
      }
    }

    dispatch(setTransportationForm({
      ...transportationForm,
      driverName: resolvedDriverName,
      phoneNumber: resolvedPhoneNumber,
      idCardNumber: documentType === 'idCard' ? resolvedDocumentNumber : '',
      passportNumber: documentType === 'passport' ? resolvedDocumentNumber : '',
    }));

    setIsProcessing(false);
    navigate(props.page.data.transitionUrl);
  };

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="bozp-phone-number">
                {getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}
              </Label.Root>
              <KeyboardWrapper
                layout="numeric"
                title={getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}
              >
                <TextField.Root
                  id="bozp-phone-number"
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.phoneNumberInputPlaceholder)}
                  value={phoneNumberValue}
                  onChange={(e) => setPhoneNumberValue(e.target.value)}
                />
              </KeyboardWrapper>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="bozp-document-type">
                {getTranslationByPath(language.translations, props.page.data.documentTypeInputLabel)}
              </Label.Root>
              <Select.Root value={documentType} onValueChange={(v) => setDocumentType(v as 'idCard' | 'passport')}>
                <Select.Trigger id="bozp-document-type" className="select mt-3">
                  <Select.Value placeholder={getTranslationByPath(language.translations, props.page.data.documentTypeInputPlaceholder)} />
                  <Select.Icon className="select-icon">
                    <ChevronDownIcon />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content role="dialog" position="popper" className="popover" sideOffset={4}>
                    <Select.Viewport className="SelectViewport">
                      <Select.Item value="idCard" className="item">
                        <Select.ItemText>{getTranslationByPath(language.translations, props.page.data.documentTypeIdCardLabel)}</Select.ItemText>
                        <Select.ItemIndicator className="item-indicator">
                          <CheckIcon />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item value="passport" className="item">
                        <Select.ItemText>{getTranslationByPath(language.translations, props.page.data.documentTypePassportLabel)}</Select.ItemText>
                        <Select.ItemIndicator className="item-indicator">
                          <CheckIcon />
                        </Select.ItemIndicator>
                      </Select.Item>
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="bozp-document-number">
                {getTranslationByPath(language.translations, props.page.data.documentNumberInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.documentNumberInputLabel)}>
                <TextField.Root
                  id="bozp-document-number"
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.documentNumberInputPlaceholder)}
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                />
              </KeyboardWrapper>
            </Box>
          </Flex>
        </Flex>
      </MainContent>

      <Footer
        backUrl={props.page.footer?.back}
        onContinue={onContinue}
        disabledBackBtn={isProcessing}
        disabledContinueBtn={isProcessing}
      />
    </>
  );
};
